import type { EmailDoc } from '@/components/email-editor';
import {
  MessageTemplateType,
  MessageTemplateChannel,
  MESSAGE_TEMPLATE_CONFIG,
  ALL_MESSAGE_TEMPLATE_TYPES,
  TEMPLATE_LANG_SENTINEL,
  getTemplateConfig,
  channelsFor,
  placeholdersFor,
  isChannelAllowed,
  isMultiNamed,
  isMessageTemplateType,
  type MessageTemplateTypeConfig,
  type TemplateMultiplicity,
  type TemplateEditableField,
} from '@masters-club/types';

/**
 * Реэкспорт единого конфига из '@masters-club/types' — НЕ дублируем.
 * Это единственный источник истины для backend и admin.
 */
export {
  MessageTemplateType,
  MessageTemplateChannel,
  MESSAGE_TEMPLATE_CONFIG,
  ALL_MESSAGE_TEMPLATE_TYPES,
  TEMPLATE_LANG_SENTINEL,
  getTemplateConfig,
  channelsFor,
  placeholdersFor,
  isChannelAllowed,
  isMultiNamed,
  isMessageTemplateType,
};
export type {
  MessageTemplateTypeConfig,
  TemplateMultiplicity,
  TemplateEditableField,
};

/** Поддерживаемые языки singleton-шаблонов (как EMAIL_TEMPLATE_LANGS). */
export type MessageTemplateLang = 'ru' | 'en' | 'uz';

/**
 * Строка шаблона сообщения из БД. Контракт backend
 * GET /admin/message-templates (singleton) и
 * GET /admin/message-templates/by-type/:type (named/singleton).
 *
 * Для multi-named (INVITATION_TELEGRAM): slug/name заполнены, lang —
 * sentinel 'ru', subject/docJson — null, текст в htmlTemplate.
 * Для singleton: slug/name — null, subject/docJson заполнены.
 */
export interface MessageTemplate {
  id: number;
  type: MessageTemplateType;
  channel: MessageTemplateChannel;
  lang: string;
  slug: string | null;
  name: string | null;
  isDefault: boolean;
  subject: string | null;
  docJson: EmailDoc | null;
  htmlTemplate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Тело PUT /admin/message-templates/:id (обновление singleton). */
export interface UpdateSingletonDto {
  subject: string;
  docJson: EmailDoc;
  htmlTemplate: string;
  isActive?: boolean;
}

/** Тело POST /admin/message-templates/named (создание именованного). */
export interface CreateNamedDto {
  type: MessageTemplateType;
  name: string;
  slug: string;
  htmlTemplate: string;
  isDefault?: boolean;
  isActive?: boolean;
}

/** Тело PUT /admin/message-templates/named/:id (обновление именованного). */
export interface UpdateNamedDto {
  name?: string;
  slug?: string;
  htmlTemplate?: string;
  isDefault?: boolean;
  isActive?: boolean;
}

/** Тело POST /admin/message-templates/preview. */
export interface PreviewDto {
  type: MessageTemplateType;
  lang: string;
  vars?: Record<string, string>;
}

/** Ответ POST /admin/message-templates/preview. */
export interface PreviewResult {
  subject: string;
  html: string;
}

/** Порядок и список языков для табов (как EMAIL_TEMPLATE_LANGS). */
export const MESSAGE_TEMPLATE_LANGS: {
  code: MessageTemplateLang;
  label: string;
}[] = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'uz', label: "O'zbekcha" },
];

/** Type guard: строка — поддерживаемый язык singleton-шаблона. */
export function isMessageTemplateLang(
  value: string,
): value is MessageTemplateLang {
  return value === 'ru' || value === 'en' || value === 'uz';
}
